import {
  onBeforeUnmount,
  onMounted,
  shallowRef,
  type Ref,
  type ShallowRef
} from 'vue'

/**
 * A dynamic import that is not started until something asks for it — the user
 * approaching the element, or the browser going idle after boot (VIP-12356).
 *
 * Vue's own lazy-hydration strategies (`hydrateOnIdle`, `hydrateOnInteraction`)
 * only delay the *hydration*: `defineAsyncComponent` starts the loader as soon
 * as the wrapper is created (runtime-core `__asyncHydrate`: load().then(...)),
 * so the chunk is downloaded during boot regardless. This gates the download
 * itself. The component is held module-wide, so a page loads it once and every
 * later instance renders it immediately.
 */
export interface DeferredImport<T> {
  /** The module's default export once loaded, null until then. */
  component: ShallowRef<T | null>
  /** Starts the import; idempotent, resolves with the default export. */
  load: () => Promise<T>
}

export const createDeferredImport = <T>(
  importer: () => Promise<{ default: T }>
): DeferredImport<T> => {
  const component = shallowRef<T | null>(null)
  let pending: Promise<T> | undefined
  const load = (): Promise<T> => {
    pending ??= importer().then(
      (module) => {
        component.value = module.default
        return module.default
      },
      (error: unknown) => {
        // let the next trigger try again; a stale-deploy failure is picked up
        // by the reload-once recovery in entry-client.ts
        pending = undefined
        throw error
      }
    )
    return pending
  }
  return { component, load }
}

/** The user is near the element and may need it soon. */
const WARMUP_EVENTS = ['pointerenter', 'focusin'] as const

/** Latest at which the import starts, from mount. */
const DEFAULT_IDLE_TIMEOUT_MS = 2000

export interface DeferredImportTrigger {
  /**
   * The user tried to use the placeholder (pointer down, touch, key). Starts
   * the import and remembers the event, so `replay` can finish the gesture on
   * the real component.
   */
  intent: (event: Event) => void
  /**
   * Call once the real component is mounted, with its root element: finishes
   * the remembered gesture — focus plus a pointer-down or the key press — so a
   * tap on the placeholder opens the real menu instead of being lost.
   */
  replay: (root: Element | null | undefined) => void
}

/**
 * Arms a deferred import on a host element: starts it when the pointer enters
 * or focus arrives, when a placeholder reports an intent, or at the latest
 * when the browser is idle. Everything is torn down on unmount and once the
 * real component has replayed the gesture.
 */
export const useDeferredImportTrigger = (
  target: Ref<HTMLElement | null>,
  deferred: DeferredImport<unknown>,
  idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS
): DeferredImportTrigger => {
  let intentEvent: Event | null = null
  let cleanup = (): void => {}

  onMounted(() => {
    if (deferred.component.value != null) return
    const el = target.value
    const warmup = (): void => void deferred.load()
    const idle = _requestIdle(warmup, idleTimeoutMs)
    for (const type of WARMUP_EVENTS) {
      el?.addEventListener(type, warmup, { once: true, passive: true })
    }
    cleanup = () => {
      idle.cancel()
      for (const type of WARMUP_EVENTS) el?.removeEventListener(type, warmup)
    }
  })
  onBeforeUnmount(() => cleanup())

  return {
    intent: (event) => {
      intentEvent = event
      void deferred.load()
    },
    replay: (root) => {
      cleanup()
      const event = intentEvent
      intentEvent = null
      const input = root?.querySelector('input')
      if (event == null || input == null) return
      input.focus()
      input.dispatchEvent(
        event instanceof KeyboardEvent
          ? new KeyboardEvent('keydown', { key: event.key, bubbles: true })
          : new MouseEvent('mousedown', { bubbles: true, cancelable: true })
      )
    }
  }
}

const _requestIdle = (
  callback: () => void,
  timeoutMs: number
): { cancel: () => void } => {
  if (typeof requestIdleCallback === 'function') {
    const id = requestIdleCallback(() => callback(), { timeout: timeoutMs })
    return { cancel: () => cancelIdleCallback(id) }
  }
  // Safari has no requestIdleCallback; the timeout alone is close enough
  const id = setTimeout(callback, timeoutMs)
  return { cancel: () => clearTimeout(id) }
}
